import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Lock } from "lucide-react";
import PasswordStrength from "./PasswordStrength";
import PasswordInput from "./PasswordInput";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

const ChangePasswordModal = ({ open, onClose }) => {
  const { toast } = useToast();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setCur(""); setPw(""); setConfirm(""); };
  const close = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast({ title: "Password too short", description: "Use at least 8 characters.", variant: "destructive" }); return; }
    if (pw !== confirm) { toast({ title: "Passwords don't match", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast({ title: "Password updated", description: "Your new password is now active." });
      close();
    } catch (err) {
      toast({ title: "Could not update", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50"><Lock className="h-6 w-6 text-indigo-700" /></div>
          <DialogTitle className="text-2xl font-bold text-[#1a1a2e]">Change password</DialogTitle>
          <DialogDescription className="text-slate-500">Enter your current password and choose a new one.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-3">
          <div className="space-y-1.5"><Label>Current password</Label><PasswordInput required value={cur} onChange={(e) => setCur(e.target.value)} placeholder="••••••••" data-testid="cur-password" /></div>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <PasswordInput required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" data-testid="new-password" />
            <PasswordStrength password={pw} />
          </div>
          <div className="space-y-1.5"><Label>Confirm new password</Label><PasswordInput required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" data-testid="confirm-password" /></div>
          <Button type="submit" disabled={busy} className="w-full rounded-xl bg-[#2f2b8f] py-6 text-sm font-semibold text-white hover:bg-[#26236f]">
            {busy ? "Updating..." : "Update password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordModal;
